/**
 * Доступ к микрофону: проверка, объяснение, восстановление.
 *
 * Порядок `ensureMicrophone()`:
 *   страница не по HTTPS / нет getUserMedia → окно с причиной (повтор бесполезен);
 *   `navigator.permissions` говорит denied → окно «Разрешите доступ в настройках»;
 *   granted → сразу слушаем;
 *   микрофон уже открыт в касании (`primeOnTap`) → ждём его результат;
 *   ученик уже отвечал (флаг `micPermissionAsked`) → решает системный запрос;
 *   иначе — объяснение, и кнопка «Разрешить» открывает микрофон в своём касании.
 *
 * Любой отказ не переводит сразу на текст: окно объясняет, что сделать
 * (по платформе и точной ошибке), и «Попробовать снова» снова открывает
 * микрофон внутри жеста. Текст — только если ученик сам выбрал «Ответить текстом».
 *
 * Флаг стирается при выходе (`api/auth.ts`).
 */

import { create } from 'zustand';

import i18n from '../i18n';
import { toast } from '../store/toastStore';
import { micBlockerBeforeTry, recentMicFailure, reportMicFailure, reportMicRecovered, type MicFailure } from './micDiagnostics';
import { micGrantedBefore, micIsOpen, primedMicrophone, primeMicrophone } from './micStream';
import { markMicrophoneAccess, resumeAudioContext } from './serverSpeech';

export const MIC_ASKED_KEY = 'micPermissionAsked';

export type MicState = 'granted' | 'denied' | 'prompt' | 'unknown';

function readAsked(): boolean {
  try {
    return localStorage.getItem(MIC_ASKED_KEY) === 'true';
  } catch {
    return false;
  }
}

function rememberAsked(): void {
  try {
    localStorage.setItem(MIC_ASKED_KEY, 'true');
  } catch {
    // Хранилище закрыто — спросим ещё раз в следующей сессии, не страшно.
  }
}

/** Последнее известное состояние — чтобы решать синхронно, в касании. */
let knownState: MicState = 'unknown';

/** Состояние разрешения у браузера; `unknown` — Permissions API нет или он не знает микрофон. */
export async function queryMicPermission(): Promise<MicState> {
  try {
    if (typeof navigator === 'undefined' || navigator.permissions?.query === undefined) return 'unknown';
    const status = await navigator.permissions.query({ name: 'microphone' as Parameters<Permissions['query']>[0]['name'] });
    knownState = status.state;
    status.onchange = () => {
      knownState = status.state;
    };
    return status.state;
  } catch {
    return 'unknown';
  }
}

// Узнать состояние заранее: в первом же касании решение синхронное.
void queryMicPermission();

/* ── Касание кнопки микрофона ───────────────────────────────────── */

/**
 * Первое синхронное действие обработчика кнопки микрофона: будит звук и,
 * если ученик уже разрешал (или ещё не видел объяснения, но браузер уже
 * разрешил), открывает микрофон прямо в жесте. Первый раз — без открытия:
 * сначала объяснение, его кнопка откроет микрофон в своём касании.
 */
export function primeOnTap(): void {
  resumeAudioContext();
  if (micBlockerBeforeTry() !== null) return;
  // Поток уже открыт — разрешение есть, ничего не запрашиваем.
  if (micIsOpen()) return;
  // Запрещено — `getUserMedia` бесполезен, решает окно настроек.
  if (knownState === 'denied') return;
  if (knownState === 'granted' || micGrantedBefore() || readAsked()) primeMicrophone();
}

/* ── Окно микрофона ─────────────────────────────────────────────── */

export type MicModal =
  | { readonly mode: 'explain' }
  | { readonly mode: 'blocked'; readonly failure: MicFailure; readonly attempts: number; readonly retrying: boolean };

interface MicPromptState {
  readonly modal: MicModal | null;
  readonly resolve: ((ok: boolean) => void) | null;
}

export const useMicPromptStore = create<MicPromptState>(() => ({ modal: null, resolve: null }));

function openModal(modal: MicModal): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    useMicPromptStore.getState().resolve?.(false);
    useMicPromptStore.setState({ modal, resolve });
  });
}

function closeModal(ok: boolean): void {
  const { resolve } = useMicPromptStore.getState();
  useMicPromptStore.setState({ modal: null, resolve: null });
  resolve?.(ok);
}

/** Повтор имеет смысл: разрешение можно выдать, микрофон — освободить. */
export function retryable(failure: MicFailure): boolean {
  return failure.kind !== 'insecure' && failure.kind !== 'unsupported';
}

/**
 * Кнопка «Разрешить» / «Попробовать снова». Обработчик клика зовёт её
 * напрямую: `getUserMedia` уходит синхронно, внутри жеста.
 */
export function allowFromModal(): void {
  const state = useMicPromptStore.getState();
  const modal = state.modal;
  if (modal === null || (modal.mode === 'blocked' && modal.retrying)) return;
  resumeAudioContext();
  // Браузер всё ещё говорит «запрещено» — `getUserMedia` упадёт сразу. Ждём,
  // пока ученик разрешит в настройках (`onchange` обновит `knownState`).
  if (modal.mode === 'blocked' && knownState === 'denied') {
    useMicPromptStore.setState({ modal: { ...modal, attempts: modal.attempts + 1, retrying: false } });
    return;
  }
  const attempt = primeMicrophone();
  const attempts = modal.mode === 'blocked' ? modal.attempts + 1 : 1;
  if (attempt === null) {
    useMicPromptStore.setState({ modal: { mode: 'blocked', failure: reportMicFailure('modal', micBlockerBeforeTry() ?? 'unsupported'), attempts, retrying: false } });
    return;
  }
  if (modal.mode === 'blocked') useMicPromptStore.setState({ modal: { ...modal, retrying: true } });
  attempt.then(
    () => {
      markMicrophoneAccess('granted');
      rememberAsked();
      knownState = 'granted';
      if (modal.mode === 'blocked') reportMicRecovered(modal.failure.stage, attempts);
      // Поток остаётся открытым — его заберёт запись (`takeMicStream`).
      closeModal(true);
    },
    (error: unknown) => {
      rememberAsked();
      const failure = recentMicFailure(2000) ?? reportMicFailure('modal', error);
      if (failure.kind === 'denied') markMicrophoneAccess('denied');
      useMicPromptStore.setState({ modal: { mode: 'blocked', failure, attempts, retrying: false } });
    },
  );
}

/** «Ответить текстом». */
export function declineFromModal(): void {
  rememberAsked();
  toast.info(i18n.t('call.micUnavailable'));
  closeModal(false);
}

/** Показать, почему микрофона нет, и дать попробовать снова. `true` — микрофон заработал. */
function showBlocked(failure: MicFailure): Promise<boolean> {
  return openModal({ mode: 'blocked', failure, attempts: 0, retrying: false });
}

/* ── Проверка перед прослушиванием ──────────────────────────────── */

let pending: Promise<boolean> | null = null;
/** Столько ждать ответа `getUserMedia`, прежде чем показать окно восстановления. */
const MIC_OPEN_TIMEOUT_MS = 10_000;

/**
 * Убедиться, что микрофон можно слушать. `true` — можно сразу начинать;
 * `false` — ученик выбрал ввод текстом (или повтор бесполезен).
 */
export function ensureMicrophone(): Promise<boolean> {
  pending ??= decide().finally(() => {
    pending = null;
  });
  return pending;
}

async function decide(): Promise<boolean> {
  const blocker = micBlockerBeforeTry();
  if (blocker !== null) return showBlocked(reportMicFailure('check', blocker));
  // Поток уже открыт (звонок, прошлое касание) — слушаем сразу, без запроса.
  if (micIsOpen()) return true;
  // Микрофон уже открыт в касании — его результат и есть ответ.
  const primed = primedMicrophone();
  if (primed !== null) {
    try {
      // iOS иногда не отвечает на `getUserMedia` вовсе — без срока кнопка
      // микрофона висела бы навсегда. Не ответил — окно с «Попробовать снова».
      await Promise.race([
        primed,
        new Promise<never>((_, reject) => {
          window.setTimeout(() => reject(Object.assign(new Error('getUserMedia did not respond in 10 s'), { name: 'TimeoutError' })), MIC_OPEN_TIMEOUT_MS);
        }),
      ]);
      markMicrophoneAccess('granted');
      rememberAsked();
      knownState = 'granted';
      return true;
    } catch (error) {
      const failure = recentMicFailure(2000) ?? reportMicFailure('prime', error);
      return showBlocked(failure);
    }
  }
  const state = await queryMicPermission();
  if (state === 'granted') {
    // Разрешено — запись откроет микрофон без окна.
    markMicrophoneAccess('granted');
    rememberAsked();
    return true;
  }
  if (state === 'denied') {
    // Запрещено — `getUserMedia` не зовём: он упадёт сразу. Окно с настройками.
    markMicrophoneAccess('denied');
    rememberAsked();
    return showBlocked(reportMicFailure('check', { name: 'NotAllowedError', message: 'permissions.query: denied' }));
  }
  // `prompt` или Permissions API нет. Микрофон уже давали на этом устройстве
  // (флаг `micPermissionGranted`) или ученик уже отвечал — пробуем сразу:
  // Safari на iOS отвечает `prompt` и после разрешения. Откажет
  // (`NotAllowedError`) — запись сообщит, и экран покажет окно.
  if (micGrantedBefore() || readAsked()) return true;
  // Выбора ещё не было — объяснение, и только его кнопка зовёт `getUserMedia`.
  return openModal({ mode: 'explain' });
}

/** Причины распознавания, которые лечатся разрешением или освобождением микрофона. */
const RECOVERABLE = new Set(['not-allowed', 'audio-capture', 'service-not-allowed']);

export function isRecoverableMicReason(reason: string): boolean {
  return RECOVERABLE.has(reason);
}

/**
 * Прослушивание кончилось отказом микрофона: окно с точной причиной и
 * «Попробовать снова». `true` — микрофон снова есть, можно слушать заново.
 */
export function recoverMicrophone(reason: string): Promise<boolean> {
  const failure = recentMicFailure() ?? reportMicFailure('listen', reason);
  return showBlocked(failure);
}
